import i18n, { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import {
    Call,
    CallCalling,
    Headphone,
    Microphone2,
    TickCircle,
} from "iconsax-react";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import DBSelect from "../../component/global/DBSelect";
import DBSpinner from "../../component/global/DBSpinner";
import DBTextArea from "../../component/global/DBTextArea";
import JalaliDateTimeInput from "../../component/global/JalaliDateTimeInput";
import { ToastContext } from "../../component/global/ToastContext";
import {
    SURVEY_CAMPAIGNS,
    SURVEY_PHONE_CALLS,
    surveyActionUrl,
    surveyCampaignUrl,
    surveyPhoneCallUrl,
} from "../../helperData/ApiAddress";
import { SurveyFormRenderer } from "../../modules/form-builder";
import { translateServerDetail } from "./translateServerDetail";
import {
    CAMPAIGN_KIND,
    PHONE_CALL,
    PHONE_INTERVIEW,
    defaultPhoneSettings,
    phoneAnswerOptions,
    phoneCallStatusLabel,
    phoneDispositionOptions,
} from "./constants";

const authHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
    "Accept-Language": i18n.language || "fa",
});

function StatCard({ label, value }) {
    return (
        <div className="phone-stat-card">
            <span>{label}</span>
            <strong>{value ?? 0}</strong>
        </div>
    );
}

export default function PhoneCallCenter({ survey, initialCampaignId = null }) {
    const toast = useContext(ToastContext);
    const [mode, setMode] = useState("board");
    const [campaignId, setCampaignId] = useState(initialCampaignId || null);
    const [campaigns, setCampaigns] = useState([]);
    const [dash, setDash] = useState(null);
    const [prompts, setPrompts] = useState([]);
    const [workstation, setWorkstation] = useState(null);
    const [replies, setReplies] = useState({});
    const [consent, setConsent] = useState(false);
    const [notes, setNotes] = useState("");
    const [disposition, setDisposition] = useState(PHONE_CALL.NO_ANSWER);
    const [callbackAt, setCallbackAt] = useState("");
    const [ivrDigit, setIvrDigit] = useState("1");
    const [loading, setLoading] = useState(false);

    const loadBoard = (id = campaignId) => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            axios.get(surveyCampaignUrl(id, "phone-dashboard"), {
                headers: authHeaders(),
            }),
            axios.get(surveyActionUrl(survey.id, "phone-prompts"), {
                headers: authHeaders(),
            }),
        ])
            .then(([dashRes, promptRes]) => {
                setDash(dashRes.data);
                setPrompts(promptRes.data || []);
            })
            .catch(() => toast(t("survey.load.error"), "danger"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        axios
            .get(SURVEY_CAMPAIGNS, {
                headers: authHeaders(),
                params: {
                    survey: survey.id,
                    kind: CAMPAIGN_KIND.PHONE,
                    page_size: 100,
                },
            })
            .then((r) => {
                const rows = r.data?.results || [];
                const phoneRows = rows.filter(
                    (row) => row.kind === CAMPAIGN_KIND.PHONE
                );
                setCampaigns(phoneRows);
                setCampaignId((current) => {
                    if (
                        current &&
                        phoneRows.some((row) => row.id === current)
                    ) {
                        return current;
                    }
                    if (
                        initialCampaignId &&
                        phoneRows.some((row) => row.id === initialCampaignId)
                    ) {
                        return initialCampaignId;
                    }
                    return phoneRows[0]?.id || null;
                });
            })
            .catch(() => setCampaigns([]));
    }, [survey.id]);

    useEffect(() => {
        if (campaignId) loadBoard(campaignId);
    }, [campaignId]);

    const claimNext = () => {
        if (!campaignId) return;
        setLoading(true);
        axios
            .post(
                `${SURVEY_PHONE_CALLS}claim-next/`,
                { campaign: campaignId },
                { headers: authHeaders() }
            )
            .then((r) => {
                setWorkstation(r.data);
                setMode("console");
                setConsent(Boolean(r.data.call?.consent_accepted));
                setNotes(r.data.call?.notes || "");
                setReplies({});
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.phone.console.claim.error"), "danger");
            })
            .finally(() => setLoading(false));
    };

    const callAction = (action, data = {}) => {
        const callId = workstation?.call?.id;
        if (!callId) return Promise.resolve();
        return axios
            .post(surveyPhoneCallUrl(callId, action), data, {
                headers: authHeaders(),
            })
            .then((r) => {
                setWorkstation(r.data);
                return r.data;
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.phone.console.load.error"), "danger");
                throw e;
            });
    };

    const savePrompts = () => {
        axios
            .put(
                surveyActionUrl(survey.id, "phone-prompts"),
                {
                    prompts: prompts.map((prompt) => ({
                        criteria_info: prompt.criteria_info,
                        script_text: prompt.script_text,
                        answer_mode: prompt.answer_mode,
                        dtmf_map: prompt.dtmf_map,
                    })),
                },
                { headers: authHeaders() }
            )
            .then((r) => {
                setPrompts(r.data);
                toast(t("survey.phone.prompts.saved"), "success");
            })
            .catch(() => toast(t("survey.save.error"), "danger"));
    };

    const criteria = workstation?.survey?.criteria || [];
    const currentPrompt = useMemo(() => {
        const infoId = workstation?.call?.prompt?.criteria_info;
        return (
            (workstation?.prompts || []).find(
                (item) => item.criteria_info === infoId
            ) || workstation?.prompts?.[0]
        );
    }, [workstation]);

    const handleSubmit = () => {
        const payload = {
            consent_accepted: consent,
            notes,
            replies: criteria.map((item) => {
                const reply = replies[item.id] || {};
                return {
                    criteria_info: item.id,
                    criteria_state: reply.state?.id || reply.state || null,
                    description: reply.description || "",
                };
            }),
        };
        callAction("submit", payload).then(() => {
            toast(t("survey.save.success"), "success");
            loadBoard();
            setMode("board");
            setWorkstation(null);
        });
    };

    const handleDisposition = () => {
        const data = { status: disposition, notes };
        if (disposition === PHONE_CALL.CALLBACK) {
            data.callback_at = callbackAt || null;
        }
        callAction("disposition", data).then(() => {
            loadBoard();
            setMode("board");
            setWorkstation(null);
        });
    };

    if (mode === "console" && workstation) {
        const call = workstation.call;
        const recipient = call.recipient || {};
        return (
            <div className="phone-operator-console">
                <div className="phone-console-toolbar">
                    <DBButton
                        type="secondary"
                        outlined
                        caption={t("survey.list.title")}
                        onClick={() => setMode("board")}
                    />
                    <DBButton
                        caption={
                            <>
                                <CallCalling size={16} />{" "}
                                {t("survey.phone.console.next")}
                            </>
                        }
                        onClick={claimNext}
                    />
                </div>
                <div className="phone-console-grid">
                    <aside className="phone-console-contact">
                        <h4>{t("survey.phone.contact")}</h4>
                        <p className="phone-contact-name">
                            {recipient.name || "—"}
                        </p>
                        <a className="phone-contact-number" href={workstation.tel_href}>
                            {recipient.phone_number}
                        </a>
                        <p className="phone-attempt">
                            {t("survey.phone.attempt", {
                                current: call.attempt_number,
                                max:
                                    workstation.config?.max_attempts ||
                                    defaultPhoneSettings().max_attempts,
                            })}
                        </p>
                        <span className="survey-status">
                            {phoneCallStatusLabel(call.status)}
                        </span>
                        <div className="phone-call-actions">
                            <DBButton
                                caption={
                                    <>
                                        <Call size={16} />{" "}
                                        {t("survey.phone.console.dial")}
                                    </>
                                }
                                onClick={() => {
                                    if (workstation.tel_href) {
                                        window.location.href = workstation.tel_href;
                                    }
                                    callAction("dial");
                                }}
                            />
                            <DBButton
                                type="secondary"
                                caption={t("survey.phone.console.connect")}
                                onClick={() => callAction("connect")}
                            />
                            <DBButton
                                type="secondary"
                                outlined
                                caption={t("survey.phone.console.hangup")}
                                onClick={() => callAction("hangup")}
                            />
                        </div>
                        <label className="phone-consent">
                            <input
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                            />
                            {t("survey.phone.console.consent")}
                        </label>
                        <DBFieldBlock title={t("survey.phone.console.notes")}>
                            <DBTextArea
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("survey.phone.disposition")}>
                            <DBSelect
                                size="large"
                                value={disposition}
                                onChange={setDisposition}
                                options={phoneDispositionOptions()}
                            />
                        </DBFieldBlock>
                        {disposition === PHONE_CALL.CALLBACK && (
                            <DBFieldBlock title={t("survey.phone.callback.at")}>
                                <JalaliDateTimeInput
                                    value={callbackAt}
                                    onChange={setCallbackAt}
                                />
                            </DBFieldBlock>
                        )}
                        <DBButton
                            type="secondary"
                            caption={t("survey.phone.disposition")}
                            onClick={handleDisposition}
                        />
                    </aside>
                    <section className="phone-console-form">
                        <div className="phone-script">
                            <h4>{t("survey.phone.console.script")}</h4>
                            <p>{workstation.greeting}</p>
                            {currentPrompt?.script_text && (
                                <p>{currentPrompt.script_text}</p>
                            )}
                        </div>
                        <SurveyFormRenderer
                            criteria={criteria}
                            replies={replies}
                            onChange={(id, patch) =>
                                setReplies((current) => ({
                                    ...current,
                                    [id]: {
                                        ...(current[id] || {}),
                                        ...patch,
                                    },
                                }))
                            }
                        />
                        <DBButton
                            caption={
                                <>
                                    <TickCircle size={16} />{" "}
                                    {t("survey.phone.console.submit")}
                                </>
                            }
                            onClick={handleSubmit}
                        />
                        {(call.interview_mode === PHONE_INTERVIEW.IVR_DTMF ||
                            call.interview_mode === PHONE_INTERVIEW.IVR_VOICE ||
                            call.interview_mode === PHONE_INTERVIEW.MIXED) && (
                            <div className="phone-ivr-sim">
                                <h4>{t("survey.phone.ivr.title")}</h4>
                                <p>{t("survey.phone.ivr.hint")}</p>
                                <p>{call.prompt?.text}</p>
                                <div className="phone-ivr-row">
                                    <DBInput
                                        size="large"
                                        value={ivrDigit}
                                        onChange={(e) =>
                                            setIvrDigit(e.target.value)
                                        }
                                    />
                                    <DBButton
                                        caption={t("survey.phone.ivr.send")}
                                        onClick={() =>
                                            callAction("dtmf", {
                                                digit: ivrDigit,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        );
    }

    return (
        <div className="phone-call-center">
            <div className="surveys-toolbar">
                <DBSelect
                    size="large"
                    placeholder={t("survey.campaign.table.name")}
                    value={campaignId}
                    onChange={setCampaignId}
                    options={campaigns.map((row) => ({
                        value: row.id,
                        label: row.name,
                    }))}
                />
                <DBButton
                    caption={
                        <>
                            <Headphone size={16} />{" "}
                            {t("survey.phone.console.start")}
                        </>
                    }
                    onClick={claimNext}
                />
            </div>
            {loading && !dash ? (
                <DBSpinner tip={t("loading")} />
            ) : (
                <>
                    <div className="phone-stat-grid">
                        <StatCard
                            label={t("survey.phone.dash.queue")}
                            value={dash?.queue}
                        />
                        <StatCard
                            label={t("survey.phone.dash.in_progress")}
                            value={dash?.in_progress}
                        />
                        <StatCard
                            label={t("survey.phone.dash.completed")}
                            value={dash?.completed}
                        />
                        <StatCard
                            label={t("survey.phone.dash.no_answer")}
                            value={dash?.no_answer}
                        />
                        <StatCard
                            label={t("survey.phone.dash.refused")}
                            value={dash?.refused}
                        />
                        <StatCard
                            label={t("survey.phone.dash.dnc")}
                            value={dash?.dnc}
                        />
                    </div>
                    <p className="survey-form-help">
                        {t("survey.phone.dash.window")}:{" "}
                        {dash?.within_calling_window
                            ? t("survey.phone.dash.window.open")
                            : t("survey.phone.dash.window.closed")}
                    </p>
                    <h4>{t("survey.phone.dash.operators")}</h4>
                    <ul className="phone-operator-list">
                        {(dash?.operators || []).map((row) => (
                            <li key={row.id}>
                                {row.name} — {row.completed}/{row.attempts}
                            </li>
                        ))}
                    </ul>
                    <h4>{t("survey.phone.prompts")}</h4>
                    {(prompts || []).map((prompt, index) => (
                        <div
                            className="phone-prompt-card"
                            key={prompt.criteria_info}
                        >
                            <strong>
                                {index + 1}. {prompt.name}
                            </strong>
                            <DBTextArea
                                rows={2}
                                value={prompt.script_text || ""}
                                onChange={(e) =>
                                    setPrompts((rows) =>
                                        rows.map((item) =>
                                            item.criteria_info ===
                                            prompt.criteria_info
                                                ? {
                                                      ...item,
                                                      script_text:
                                                          e.target.value,
                                                  }
                                                : item
                                        )
                                    )
                                }
                            />
                            <DBSelect
                                size="large"
                                value={prompt.answer_mode}
                                onChange={(answer_mode) =>
                                    setPrompts((rows) =>
                                        rows.map((item) =>
                                            item.criteria_info ===
                                            prompt.criteria_info
                                                ? { ...item, answer_mode }
                                                : item
                                        )
                                    )
                                }
                                options={phoneAnswerOptions()}
                            />
                            <label className="phone-audio-upload">
                                <Microphone2 size={16} />
                                {t("survey.phone.prompts.audio")}
                                <input
                                    type="file"
                                    accept="audio/*"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        if (!file) return;
                                        const body = new FormData();
                                        body.append("file", file);
                                        body.append(
                                            "criteria_info",
                                            prompt.criteria_info
                                        );
                                        body.append("kind", "prompt");
                                        axios
                                            .post(
                                                surveyActionUrl(
                                                    survey.id,
                                                    "phone-audio"
                                                ),
                                                body,
                                                { headers: authHeaders() }
                                            )
                                            .then(() =>
                                                toast(
                                                    t("survey.phone.prompts.saved"),
                                                    "success"
                                                )
                                            )
                                            .catch(() =>
                                                toast(
                                                    t("survey.save.error"),
                                                    "danger"
                                                )
                                            );
                                    }}
                                />
                            </label>
                        </div>
                    ))}
                    <DBButton
                        caption={t("survey.phone.prompts.save")}
                        onClick={savePrompts}
                    />
                </>
            )}
        </div>
    );
}
